<template>
  <div style="margin:20px">
    <div style="width: 100%; background-color: #eee; border: 1px dotted grey; margin-bottom: 25px;">
      <span style="font-size: 26px; font-weight: bold">Driver Scorecard</span><br/>
      <span style="font-style: italic; font-size: 18px">Individual driver performance metrics and KPIs from Samsara Safety, Samsara Idling, TBN Dispatch Pricing, and TBN Driver Availability</span>
    </div>

    <div style="position: relative; font-size: 20px; font-weight: bold;" class="align-right d-flex align-items-center justify-content-center">
      <a href="#" class="white-link" v-on:click.prevent="shift_date_range(-1)"><</a>
      &nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_fromDate" :config="optionsFrom" :placeholder="details_fromDate"/>
      &nbsp;to&nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_toDate" :config="optionsTo" :placeholder="details_toDate"/>
      &nbsp;<a href="#" class="white-link" v-on:click.prevent="shift_date_range(1)">></a>
      &nbsp;&nbsp;|&nbsp;&nbsp;
      <span>Driver:</span>&nbsp;
      <b-form-select v-if="driver_options.length > 0" v-model="selected_driver_id" :options="driver_options" style="font-size: 18px; font-weight: bold; width:220px"/>
      <span v-else><b-spinner style="width: 2rem; height: 2rem;" label="Loading..."></b-spinner></span>
      &nbsp;&nbsp;|&nbsp;&nbsp;
      <b-button v-on:click.prevent="load_scorecard()" :disabled="loading || !selected_driver_id">Load Scorecard</b-button>
      &nbsp;&nbsp;
      <b-button size="sm" variant="outline-secondary" v-on:click.prevent="clear_driver_list_cache()" :disabled="clearing_cache">Clear driver list cache</b-button>
    </div>

    <div v-if="includes_current_month" class="text-center text-muted mb-2" style="font-style: italic;">
      Current month data is calculated on-the-fly and may take longer to load.
    </div>

    <div v-if="loading" style="text-align: center; margin: 50px;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

    <div v-else-if="scorecard_data" style="margin: 20px;">
      <b-card>
        <div class="d-flex flex-wrap align-items-center justify-content-center gap-3 mb-2">
          <h3 class="mb-0">{{ scorecard_data.driver_name || 'Driver Scorecard' }}</h3>
          <div class="ml-4">
            <h5 class="mb-0">Miles Driven</h5>
            <h3 class="mb-0">{{ formatNumber(scorecard_data.miles_driven, 2) || formatNumber(scorecard_data.distance_traveled_miles, 2) || '0.00' }}</h3>
          </div>
        </div>
        <p>{{ details_fromDate }} to {{ details_toDate }} ({{ scorecard_data.date_range_days || 0 }} days)<span v-if="scorecard_data.current_month_included" class="text-muted ml-2">— Current month computed on-the-fly; last 5 days excluded.</span></p>
        
        <hr/>
        
        <h4>Risk Metrics</h4>
        <div class="scorecard-metrics-row">
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Risk Score</h5>
            <h2 :class="getRiskScoreClass(scorecard_data.risk_score)">
              {{ scorecard_data.risk_score || 'N/A' }}
            </h2>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Risk Rank</h5>
            <h2 :class="getRiskRankClass(scorecard_data.risk_score_rank)">
              {{ scorecard_data.risk_score_rank || 'N/A' }}
            </h2>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Speeding %</h5>
            <h3>{{ formatNumber(scorecard_data.speeding_percentage, 2) || 0 }}%</h3>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Distracted Driving per 10k</h5>
            <h3>{{ formatNumber(scorecard_data.distracted_driving_percentage, 2) || 0 }}</h3>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Following Dist. per 10k</h5>
            <h3>{{ formatNumber(scorecard_data.following_distance_per_10k_miles, 2) || 0 }}</h3>
          </b-card>
        </div>

        <hr/>

        <h4>Idling & Fuel Efficiency</h4>
        <div class="scorecard-metrics-row">
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Idling %</h5>
            <h3 :class="getIdlingClass(scorecard_data.idling_percentage)">
              {{ formatNumber(scorecard_data.idling_percentage, 2) }}%
            </h3>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>MPG</h5>
            <h3 :class="getMpgClass(scorecard_data.miles_per_gallon)">{{ formatNumber(scorecard_data.miles_per_gallon, 2) || 'N/A' }}</h3>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Idle Hrs</h5>
            <h3>{{ formatNumber(scorecard_data.idle_hours, 2) }}</h3>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Run Hrs</h5>
            <h3>{{ formatNumber(scorecard_data.run_hours, 2) }}</h3>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Gallons</h5>
            <h3>{{ formatNumber(scorecard_data.fuel_consumed_gallons, 2) || '0.00' }}</h3>
          </b-card>
        </div>

        <hr/>

        <h4>Dispatch (from TBN)</h4>
        <div class="scorecard-metrics-row">
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Customer Requests</h5>
            <h3 :class="(scorecard_data.customer_requests || 0) === 0 ? 'text-warning' : 'text-success'">{{ scorecard_data.customer_requests || 0 }}</h3>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Bus Days</h5>
            <h3>{{ scorecard_data.bus_days || 0 }}</h3>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Dispatch Miles</h5>
            <h3>{{ formatNumber(scorecard_data.total_miles, 2) }}</h3>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Weekend Avail.</h5>
            <h3>{{ formatNumber(scorecard_data.weekend_availability_percentage, 2) || 0 }}%</h3>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Cust. Req. per 10k</h5>
            <h3 :class="(scorecard_data.customer_requests_per_10k_miles || 0) === 0 ? 'text-warning' : 'text-success'">{{ formatNumber(scorecard_data.customer_requests_per_10k_miles, 2) || 0 }}</h3>
          </b-card>
        </div>
      </b-card>
    </div>

    <div v-else-if="!loading && selected_driver_id" style="text-align: center; margin: 50px; color: #666;">
      <p>No scorecard data available. Click "Load Scorecard" to fetch data.</p>
    </div>
  </div>
</template>

<script>
import {mapState} from "vuex"
import {justDate} from '../helpers/dates'
import moment from 'moment'
import {get_driver_scorecard, tbn_get_drivers_scorecard_list, tbn_clear_scorecard_driver_list_cache} from '../networking/tbn_apis'

export default {
  name: 'driver_scorecard',

  computed: {
    ...mapState({
      user: state => state.profile.user
    }),
    includes_current_month() {
      const current = moment().format('YYYY-MM')
      const startM = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM')
      const endM = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM')
      return startM <= current && endM >= current
    },
    /** Picker bounds + only 1st of each month selectable (bootstrap-datetimepicker enabledDates) */
    optionsFrom() {
      const minD = moment().subtract(3, 'years').startOf('month')
      const maxD = moment()
      return {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: minD,
        maxDate: maxD,
        enabledDates: this.buildFirstOfMonthDates(minD, maxD),
        keepOpen: false,
        showClear: true,
        showClose: true,
      }
    },
    /** Last day of each month, or today when ending a partial current month */
    optionsTo() {
      const minD = moment().subtract(3, 'years').startOf('month')
      const maxD = moment()
      return {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: minD,
        maxDate: maxD,
        enabledDates: this.buildLastOfMonthOrTodayDates(minD, maxD),
        keepOpen: false,
        showClear: true,
        showClose: true,
      }
    },
  },

  data() {
    return {
      loading: false,
      clearing_cache: false,
      scorecard_data: null,
      selected_driver_id: null,
      driver_options: [],
      total_drivers: 0,
      details_fromDate: justDate((moment().date() >= 7 ? moment().subtract(1, 'month') : moment().subtract(2, 'months')).startOf('month')),
      details_toDate: justDate((moment().date() >= 7 ? moment().subtract(1, 'month') : moment().subtract(2, 'months')).endOf('month')),
    }
  },

  created() {
    this.load_drivers()
  },

  watch: {
    details_toDate() {
      const today = moment()
      if (moment(this.details_toDate, 'MM-DD-YYYY').isAfter(today))
        this.details_toDate = justDate(today)

      if (moment(this.details_fromDate, 'MM-DD-YYYY') > moment(this.details_toDate, 'MM-DD-YYYY'))
        this.details_fromDate = justDate(moment(this.details_toDate, 'MM-DD-YYYY').startOf('month'))

      const months = moment(this.details_toDate, 'MM-DD-YYYY').diff(moment(this.details_fromDate, 'MM-DD-YYYY'), 'months', true)
      if (months > 12) {
        this.details_fromDate = moment(this.details_toDate, 'MM-DD-YYYY').subtract(12, 'months').startOf('month').format('MM-DD-YYYY')
      }
    },
    details_fromDate() {
      if (moment(this.details_fromDate, 'MM-DD-YYYY') > moment(this.details_toDate, 'MM-DD-YYYY'))
        this.details_toDate = justDate(moment(this.details_fromDate, 'MM-DD-YYYY').endOf('month'))

      const today = moment()
      if (moment(this.details_toDate, 'MM-DD-YYYY').isAfter(today))
        this.details_toDate = justDate(today)

      const months = moment(this.details_toDate, 'MM-DD-YYYY').diff(moment(this.details_fromDate, 'MM-DD-YYYY'), 'months', true)
      if (months > 12) {
        this.details_toDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(12, 'months').endOf('month').format('MM-DD-YYYY')
      }
    },
  },

  methods: {
    /** First calendar day of each month between minD and maxD (inclusive) */
    buildFirstOfMonthDates(minD, maxD) {
      const out = []
      let m = minD.clone().startOf('month')
      const lastMonthStart = maxD.clone().startOf('month')
      while (m.isSameOrBefore(lastMonthStart, 'month')) {
        const first = m.clone().date(1)
        if (first.isSameOrBefore(maxD, 'day') && first.isSameOrAfter(minD, 'day')) {
          out.push(first.clone())
        }
        m.add(1, 'month')
      }
      return out
    },
    /** Last day of each month in range; also "today" for the current month when today is not month-end */
    buildLastOfMonthOrTodayDates(minD, maxD) {
      const out = []
      const today = moment().startOf('day')
      let m = minD.clone().startOf('month')
      const lastMonthStart = maxD.clone().startOf('month')
      while (m.isSameOrBefore(lastMonthStart, 'month')) {
        const last = m.clone().endOf('month').startOf('day')
        if (last.isSameOrAfter(minD, 'day') && last.isSameOrBefore(maxD, 'day')) {
          out.push(last.clone())
        }
        if (m.isSame(today, 'month') && today.isBefore(last, 'day')) {
          out.push(today.clone())
        }
        m.add(1, 'month')
      }
      return out
    },

    async clear_driver_list_cache() {
      if (!this.user || !this.user.tenant) return
      this.clearing_cache = true
      try {
        await tbn_clear_scorecard_driver_list_cache(this.user.tenant)
        this.$bvToast.toast('Driver list cache cleared. Reload the page to rebuild.', { variant: 'success', autoHideDelay: 4000 })
        await this.load_drivers()
      } catch (e) {
        this.$bvToast.toast((e.response && e.response.data && e.response.data.message) || 'Failed to clear cache', { variant: 'danger' })
      } finally {
        this.clearing_cache = false
      }
    },

    async load_drivers() {
      try {
        const response = await tbn_get_drivers_scorecard_list(this.user.tenant)
        const {value: {data}} = response
        let drivers = []
        if (Array.isArray(data)) {
          drivers = data
        } else if (data && data.data && Array.isArray(data.data)) {
          drivers = data.data
        }
        const filteredDrivers = drivers.filter(driver => driver.value && driver.value !== '')
        const sorted = [...filteredDrivers].sort((a, b) => {
          const textA = (a.text || a.fullName || a.name || '').toLowerCase()
          const textB = (b.text || b.fullName || b.name || '').toLowerCase()
          return textA.localeCompare(textB)
        })
        this.total_drivers = sorted.length
        this.driver_options = [
          { value: null, text: 'Select Driver' },
          ...sorted.map(driver => ({
            value: driver.value,
            text: driver.text || driver.fullName || driver.name || `${driver.firstName || ''} ${driver.lastName || ''}`.trim()
          }))
        ]
      } catch (error) {
        console.error('Error loading drivers:', error)
        this.driver_options = [{ value: null, text: 'Select Driver' }]
      }
    },

    shift_date_range(shift) {
      const newFrom = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'month')
      let newTo = moment(this.details_toDate, 'MM-DD-YYYY').add(shift, 'month').endOf('month')
      const today = moment()
      if (newTo.isAfter(today)) newTo = today.clone()
      if (newFrom.isAfter(today)) return
      this.details_fromDate = newFrom.format('MM-DD-YYYY')
      this.details_toDate = newTo.format('MM-DD-YYYY')
    },

    async load_scorecard() {
      if (!this.selected_driver_id) return

      this.loading = true
      this.scorecard_data = null

      try {
        const start_month = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM')
        const end_month = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM')

        const response = await get_driver_scorecard(
          this.user.tenant,
          this.selected_driver_id,
          start_month,
          end_month
        )

        const responseData = (response && response.value && response.value.data) || (response && response.data)

        if (responseData) {
          if (responseData.error) {
            this.$bvToast.toast(responseData.error, {
              title: 'Scorecard Error', variant: 'warning', solid: true, autoHideDelay: 8000
            })
          } else if (responseData.results) {
            this.scorecard_data = responseData.results
          } else {
            this.$bvToast.toast('No scorecard data available for this driver and date range', {
              title: 'No Data', variant: 'warning', solid: true
            })
          }
        } else {
          this.$bvToast.toast('Unexpected response format from server', {
            title: 'Error', variant: 'danger', solid: true
          })
        }
      } catch (error) {
        console.error('Error loading scorecard:', error)
        const serverMsg = (error.response && error.response.data && error.response.data.error)
            || (error.response && error.response.data && error.response.data.data && error.response.data.data.error)
        this.$bvToast.toast(serverMsg || error.message || 'Error loading scorecard data', {
          title: 'Error', variant: 'danger', solid: true, autoHideDelay: 8000
        })
      } finally {
        this.loading = false
      }
    },

    formatNumber(value, decimals = 2) {
      if (value === null || value === undefined) return '0.00'
      return Number(value).toFixed(decimals)
    },

    getRiskScoreClass(riskScore) {
      if (riskScore === null || riskScore === undefined) return ''
      const score = Number(riskScore)
      if (score >= 80) return 'text-success'
      if (score >= 50) return 'text-warning'
      return 'text-danger'
    },

    getRiskRankClass(riskRank) {
      if (riskRank === null || riskRank === undefined || this.total_drivers === 0) return ''
      const percentile = (Number(riskRank) / this.total_drivers) * 100
      if (percentile <= 25) return 'text-success'
      if (percentile <= 75) return 'text-warning'
      return 'text-danger'
    },

    getIdlingClass(idlingPercentage) {
      if (idlingPercentage === null || idlingPercentage === undefined) return ''
      const pct = Number(idlingPercentage)
      if (pct < 21.8) return 'text-success'
      if (pct <= 30) return 'text-warning'
      return 'text-danger'
    },

    getMpgClass(mpg) {
      if (mpg === null || mpg === undefined) return 'text-danger'
      const value = Number(mpg)
      if (value >= 7.1) return 'text-success'
      if (value >= 6.6) return 'text-warning'
      return 'text-danger'
    }
  }
}
</script>

<style scoped>
.white-link {
  color: white;
  text-decoration: none;
  font-size: 24px;
}
.white-link:hover {
  color: #ccc;
}

.scorecard-metrics-row {
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-start;
  gap: 1.1rem;
  margin-bottom: 1.35rem;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 8px;
  -webkit-overflow-scrolling: touch;
}
.metric-card-inline {
  flex: 0 0 auto;
  min-width: 195px;
  padding: 1.1rem 1.25rem !important;
  margin-bottom: 0 !important;
}
.metric-card-inline h5 {
  font-size: 1.1rem;
  margin-bottom: 0.4rem;
  line-height: 1.2;
}
.metric-card-inline h2 {
  font-size: 1.85rem;
}
.metric-card-inline h3 {
  font-size: 1.6rem;
}
</style>
